/**
 * Contacts Panel Component
 * Displays list of contacts with favorite and call actions
 */

import React, { useState, useMemo } from 'react';
import {
  Input,
  List,
  Avatar,
  Button,
  Spin,
  Typography,
  Divider,
  Empty,
  Tooltip,
  message,
} from 'antd';
import {
  StarOutlined,
  StarFilled,
  VideoCameraOutlined,
  MessageOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  useContacts,
  useFavorites,
  useAddToFavorites,
  useRemoveFromFavorites,
} from '@/hooks/useContacts';
import { useFindOrCreateOneToOne } from '@/hooks/useChat';
import { useStartVideoCall } from '@/hooks/useVideoCall';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import type { Contact, FavoriteContact } from '@/api/types/contacts.types';
import './ContactsPanel.css';

const { Text } = Typography;
const { Search } = Input;

interface ContactsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isInline?: boolean; // Inline mode for ModernLayout
}

export const ContactsPanel: React.FC<ContactsPanelProps> = ({
  isOpen,
  onClose,
  isInline = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch data
  const { data: contacts = [], isLoading: loadingContacts } = useContacts();
  const { data: favorites = [], isLoading: loadingFavorites } = useFavorites();

  // Mutations
  const { mutate: addToFavorites } = useAddToFavorites();
  const { mutate: removeFromFavorites } = useRemoveFromFavorites();
  const { mutate: startVideoCall } = useStartVideoCall();
  const { mutate: findOrCreateOneToOne, isPending: creatingChat } =
    useFindOrCreateOneToOne();

  // Create a set of favorite IDs for quick lookup
  const favoriteIds = useMemo(
    () => new Set(favorites.map((f) => f.id)),
    [favorites]
  );

  // Filter contacts by search query and exclude favorites
  const filteredContacts = useMemo(() => {
    // First, exclude contacts that are already in favorites
    const nonFavoriteContacts = contacts.filter(
      (contact) => !favoriteIds.has(contact.__meta__.id)
    );

    // If no search query, return all non-favorite contacts
    if (!searchQuery.trim()) return nonFavoriteContacts;

    // Filter by search query
    const query = searchQuery.toLowerCase();
    return nonFavoriteContacts.filter(
      (contact) =>
        contact.first_name.toLowerCase().includes(query) ||
        contact.last_name.toLowerCase().includes(query) ||
        contact.email.toLowerCase().includes(query) ||
        contact.username.toLowerCase().includes(query)
    );
  }, [contacts, searchQuery, favoriteIds]);

  // Filter favorites by search query
  const filteredFavorites = useMemo(() => {
    if (!searchQuery.trim()) return favorites;

    const query = searchQuery.toLowerCase();
    return favorites.filter(
      (contact) =>
        contact.first_name.toLowerCase().includes(query) ||
        contact.last_name.toLowerCase().includes(query) ||
        contact.email.toLowerCase().includes(query) ||
        contact.username.toLowerCase().includes(query)
    );
  }, [favorites, searchQuery]);

  const handleToggleFavorite = (userId: string, isFavorite: boolean) => {
    if (isFavorite) {
      removeFromFavorites(userId);
    } else {
      addToFavorites(userId);
    }
  };

  const handleCall = (contact: Contact | FavoriteContact) => {
    const displayName = user?.full_name || user?.email || 'Anonymous';
    const roomName = `call-with-${contact.username}`;

    startVideoCall(
      {
        display_name: displayName,
        room_name: roomName,
        notify_team: true,
      },
      {
        onSuccess: (response) => {
          // Navigate to video call in same window
          navigate(`/apps/video-call?room=${response.room_name}`);
          onClose(); // Close contacts panel
        },
      }
    );
  };

  const handleOpenChat = (contact: Contact | FavoriteContact) => {
    const userId = '__meta__' in contact ? contact.__meta__.id : contact.id;

    findOrCreateOneToOne(userId, {
      onSuccess: (chat) => {
        // Navigate to the specific chat
        navigate(`/apps/chat/${chat.id}`);
        onClose(); // Close contacts panel
      },
      onError: (error) => {
        console.error('Failed to open chat:', error);
        message.error('Failed to open chat. Please try again.');
      },
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
  };

  const renderContactItem = (
    contact: Contact | FavoriteContact,
    isFavorite: boolean
  ) => {
    const userId = '__meta__' in contact ? contact.__meta__.id : contact.id;
    const color =
      '__meta__' in contact ? contact.__meta__.color : contact.color;

    return (
      <List.Item key={userId} className="contacts-panel-list-item">
        <div className="contact-item-content">
          {/* Left: Avatar */}
          <Avatar
            className="contact-item-avatar"
            style={{ backgroundColor: color || '#1890ff' }}
            icon={<UserOutlined />}
          >
            {getInitials(contact.first_name, contact.last_name)}
          </Avatar>

          {/* Middle: Star + Name (with email on hover) */}
          <Tooltip title={contact.email} placement="top">
            <div className="contact-item-info">
              <Button
                type="text"
                size="small"
                className="contact-item-star"
                icon={
                  isFavorite ? (
                    <StarFilled style={{ color: '#faad14' }} />
                  ) : (
                    <StarOutlined />
                  )
                }
                onClick={() => handleToggleFavorite(userId, isFavorite)}
              />
              <span className="contact-item-name">
                {contact.first_name} {contact.last_name}
              </span>
            </div>
          </Tooltip>

          {/* Right: Actions */}
          <div className="contact-item-actions">
            <Button
              type="text"
              size="small"
              icon={<MessageOutlined />}
              onClick={() => handleOpenChat(contact)}
              title="Open chat"
              loading={creatingChat}
              disabled={creatingChat}
            />
            <Button
              type="text"
              size="small"
              icon={<VideoCameraOutlined />}
              onClick={() => handleCall(contact)}
              title="Start video call"
            />
          </div>
        </div>
      </List.Item>
    );
  };

  return (
    <>
      {/* Backdrop - only show in overlay mode */}
      {!isInline && (
        <div
          className={`contacts-panel-backdrop ${isOpen ? 'contacts-backdrop-open' : 'contacts-backdrop-closed'}`}
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`contacts-panel ${isInline ? 'contacts-panel-inline' : ''} ${isOpen ? 'contacts-panel-open' : 'contacts-panel-closed'}`}
      >
        <div className="contacts-panel-header">
          <h2 className="contacts-panel-title">Contacts</h2>
          <Button type="text" onClick={onClose}>
            ✕
          </Button>
        </div>

        <div className="contacts-panel-content">
          {/* Search */}
          <div className="contacts-panel-search">
            <Search
              placeholder="Search contacts..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
          </div>

          {loadingContacts || loadingFavorites ? (
            <div className="contacts-panel-loading">
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* Favorites Section */}
              {filteredFavorites.length > 0 && (
                <>
                  <Divider orientation="left">
                    <StarFilled style={{ color: '#faad14', marginRight: 8 }} />
                    Favorites ({filteredFavorites.length})
                  </Divider>
                  <List
                    className="contacts-panel-list"
                    dataSource={filteredFavorites}
                    renderItem={(contact) => renderContactItem(contact, true)}
                  />
                </>
              )}

              {/* All Contacts Section */}
              <Divider orientation="left">
                All Contacts ({filteredContacts.length})
              </Divider>
              {filteredContacts.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No contacts found"
                />
              ) : (
                <List
                  className="contacts-panel-list"
                  dataSource={filteredContacts}
                  renderItem={(contact) =>
                    renderContactItem(
                      contact,
                      favoriteIds.has(contact.__meta__.id)
                    )
                  }
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
